import { useEffect } from 'react';
import { defaultPasswordRules, type PasswordRules } from '@lib/passwordRules';
import { prefersReducedMotion } from '@utils/prefersReducedMotion';

type ToastType = 'success' | 'info' | 'warning' | 'error';

interface ToastOptions {
  type: ToastType;
  title?: string;
  message?: string;
  id?: string;
  persistent?: boolean;
}

declare global {
  interface Window {
    Toast?: (opts: ToastOptions) => void;
  }
}

type FieldRef = {
  el: HTMLElement | null;
  err: HTMLElement | null;
};

type InlineValidationOptions = {
  passwordRules?: PasswordRules;
};

export default function useInlineValidation(formId: string, options: InlineValidationOptions = {}) {
  useEffect(() => {
    const form = document.getElementById(formId) as HTMLFormElement | null;
    if (!form) return;

    const fields: Record<string, FieldRef> = {
      name: {
        el: document.getElementById('vName'),
        err: document.getElementById('err-name'),
      },
      email: {
        el: document.getElementById('vEmail'),
        err: document.getElementById('err-email'),
      },
      pass: {
        el: document.getElementById('vPass'),
        err: document.getElementById('err-pass'),
      },
      pass2: {
        el: document.getElementById('vPass2'),
        err: document.getElementById('err-pass2'),
      },
      age: {
        el: document.getElementById('vAge'),
        err: document.getElementById('err-age'),
      },
      country: {
        el: document.getElementById('vCountry'),
        err: document.getElementById('err-country'),
      },
      terms: {
        el: document.getElementById('vTerms'),
        err: document.getElementById('err-terms'),
      },
    };

    const summary = document.getElementById('formSummary');

    function showError(f: FieldRef, msg: string) {
      if (!f.el) return;
      f.el.setAttribute('aria-invalid', 'true');
      if (f.err) {
        f.err.hidden = false;
        f.err.textContent = msg;
        const id = f.err.id || `${(f.el as HTMLElement).id}-err`;
        f.err.id = id;
        (f.el as HTMLElement).setAttribute('aria-describedby', id);
      }
    }

    function clearError(f: FieldRef) {
      if (!f.el) return;
      f.el.removeAttribute('aria-invalid');
      f.el.classList.remove('is-valid');
      if (f.err) {
        f.err.hidden = true;
        f.err.textContent = '';
      }
      f.el.removeAttribute('aria-describedby');
    }

    function markValid(f: FieldRef) {
      if (!f.el) return;
      f.el.classList.add('is-valid');
    }

    const passwordRules: Required<PasswordRules> = {
      minLength: options.passwordRules?.minLength ?? defaultPasswordRules.minLength,
      requireNumber: options.passwordRules?.requireNumber ?? defaultPasswordRules.requireNumber,
    };

    function isEmail(v: string) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    function hasNumber(v: string) {
      return /\d/.test(v);
    }

    function passwordIsValid(v: string) {
      if (v.length < passwordRules.minLength) return false;
      if (passwordRules.requireNumber && !hasNumber(v)) return false;
      return true;
    }

    function validate() {
      const errors: string[] = [];

      const name = ((fields.name.el as HTMLInputElement)?.value || '').trim();
      clearError(fields.name);
      if (!name) {
        showError(fields.name, 'Name is required.');
        errors.push('Name is required.');
      } else if (name.length < 2) {
        showError(fields.name, 'Use at least 2 characters.');
        errors.push('Name too short.');
      } else markValid(fields.name);

      const email = ((fields.email.el as HTMLInputElement)?.value || '').trim();
      clearError(fields.email);
      if (!email) {
        showError(fields.email, 'Email is required.');
        errors.push('Email is required.');
      } else if (!isEmail(email)) {
        showError(fields.email, 'Enter a valid email address.');
        errors.push('Invalid email format.');
      } else markValid(fields.email);

      const pw = (fields.pass.el as HTMLInputElement)?.value || '';
      clearError(fields.pass);
      if (!pw) {
        showError(fields.pass, 'Password is required.');
        errors.push('Password is required.');
      } else if (!passwordIsValid(pw)) {
        const numberHint = passwordRules.requireNumber ? ' and include a number' : '';
        showError(fields.pass, `Min ${passwordRules.minLength} chars${numberHint}.`);
        errors.push('Weak password.');
      } else markValid(fields.pass);

      const pw2 = (fields.pass2.el as HTMLInputElement)?.value || '';
      clearError(fields.pass2);
      if (!pw2) {
        showError(fields.pass2, 'Please confirm your password.');
        errors.push('Confirm your password.');
      } else if (pw2 !== pw) {
        showError(fields.pass2, 'Passwords do not match.');
        errors.push('Passwords mismatch.');
      } else markValid(fields.pass2);

      const age = (((fields.age.el as HTMLInputElement)?.value || '') as string).trim();
      clearError(fields.age);
      if (age) {
        const n = Number(age);
        if (!(n >= 18 && n <= 120)) {
          showError(fields.age, 'Age must be between 18 and 120.');
          errors.push('Age out of range.');
        } else markValid(fields.age);
      }

      clearError(fields.country);
      if (!(((fields.country.el as HTMLSelectElement)?.value || '') as string)) {
        showError(fields.country, 'Please select a country.');
        errors.push('Country required.');
      } else markValid(fields.country);

      if (fields.terms.err) fields.terms.err.hidden = true;
      if (!(fields.terms.el as HTMLInputElement)?.checked) {
        if (fields.terms.err) {
          fields.terms.err.hidden = false;
          fields.terms.err.textContent = 'You must accept the terms.';
        }
        errors.push('Terms required.');
      }

      if (errors.length) {
        if (summary) {
          summary.hidden = false;
          summary.innerHTML = `<b>Fix the following:</b><ul>${errors
            .map((e) => `<li>${e}</li>`)
            .join('')}</ul>`;
        }
      } else {
        if (summary) {
          summary.hidden = true;
          summary.textContent = '';
        }
      }
      return errors.length === 0;
    }

    const onInput = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const id = (target as HTMLElement).id;
      if (id && Object.values(fields).some((f) => f.el === target)) {
        validate();
      }
    };

    const onSubmit = (e: Event) => {
      e.preventDefault();
      if (validate()) {
        window.Toast?.({
          type: 'success',
          title: 'All good',
          message: 'Form submitted.',
        });
        form.reset();
        Object.values(fields).forEach(clearError);
      } else {
        const firstErr = form.querySelector(
          '[aria-invalid="true"], .field-error:not([hidden])'
        ) as HTMLElement | null;
        firstErr?.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'center',
        });
      }
    };

    const onReset = () => {
      if (summary) {
        summary.hidden = true;
        summary.textContent = '';
      }
      Object.values(fields).forEach(clearError);
    };

    form.addEventListener('input', onInput as EventListener);
    form.addEventListener('submit', onSubmit as EventListener);
    form.addEventListener('reset', onReset as EventListener);

    return () => {
      form.removeEventListener('input', onInput as EventListener);
      form.removeEventListener('submit', onSubmit as EventListener);
      form.removeEventListener('reset', onReset as EventListener);
    };
  }, [formId, options.passwordRules?.minLength, options.passwordRules?.requireNumber]);
}
